import React from 'react';
import { InjectedRouter } from 'react-router';

import CoachingCoordinatorView from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorView';
import CoachingConstants from 'coaching/CoachingConstants';
import CurrentUser from 'auth/CurrentUser';
import { createCoachingSession } from 'coaching/actions/CoachingActions';
import GetCoachesContainer from 'coaching/containers/GetCoachesContainer';
import GetParticipantsContainer from 'coaching/containers/GetParticipantsContainer';
import { CoachingUserType, CoachingSessionInput } from 'globalTypes';
import { GetParticipantList_coaching_getParticipantList_participants } from 'coaching/queries/__generated__/GetParticipantList';
import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';
import ToastActions from 'actions/ToastActions';
import { debounce } from 'lodash';

type Props = {
  router: InjectedRouter;
};

type State = {
  selectedParticipantUuid?: string;
  // The contents of the search input component
  nameSearchInputText: string;
  // The text that's being currently searched for
  nameQuery: string;
};

export default class CoachingCoordinatorContainer extends React.Component<Props, State> {
  state: State = {
    selectedParticipantUuid: undefined,
    nameQuery: '',
    nameSearchInputText: '',
  };

  selectParticipant = (
    participant: GetParticipantList_coaching_getParticipantList_participants
  ) => {
    this.setState({ selectedParticipantUuid: participant.uuid });
  };

  handleCreateCoachingSession = (input: CoachingSessionInput) => {
    const { selectedParticipantUuid } = this.state;

    if (!selectedParticipantUuid) {
      console.error('Tried to create coaching session without a selected participant');
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to create a coaching session without a selected participant'
      );
      return Promise.resolve();
    }

    return createCoachingSession(
      input,
      [
        {
          query: GET_COACHING_SESSIONS_QUERY,
          variables: {
            userUuid: selectedParticipantUuid,
            userType: CoachingUserType.participant,
          },
        },
      ],
      () => ToastActions.showSuccessToast('Created coaching session')
    );
  };

  searchByName = debounce(
    () => {
      this.setState({
        nameQuery: this.state.nameSearchInputText.trim(),
      });
    },
    400,
    { trailing: true }
  );

  nameQueryChanged = (newQuery: string) => {
    this.setState(
      {
        nameSearchInputText: newQuery,
      },
      this.searchByName
    );
  };

  render() {
    const user = CurrentUser.isSignedIn() && CurrentUser.getUser();
    if (!user.hasFeature('coaching_coordinator.web.may-20')) {
      this.props.router.push(CoachingConstants.DEFAULT_SIGNED_IN_ROUTE);
      return null;
    }

    const { selectedParticipantUuid } = this.state;

    return (
      <GetCoachesContainer query={this.state.nameQuery}>
        {({
          coaches,
          endCursor: coachEndCursor,
          error,
          hasNextPage: coachListHasNextPage,
          loading: loadingCoaches,
          onNextPage: onNextCoachPage,
        }) => {
          const coachesErrorMessage = error?.message;
          return (
            <GetParticipantsContainer query={this.state.nameQuery}>
              {({
                endCursor: participantEndCursor,
                error,
                hasNextPage: participantListHasNextPage,
                loading: loadingParticipantsAndCancellations,
                onNextPage: onNextParticipantPage,
                participants,
              }) => {
                const participantsOrCancellationsErrorMessage = error?.message;
                return (
                  <CoachingCoordinatorView
                    loadingParticipants={loadingParticipantsAndCancellations}
                    loadingCoaches={loadingCoaches}
                    participantsError={participantsOrCancellationsErrorMessage}
                    coachesError={coachesErrorMessage}
                    participants={participants}
                    coaches={coaches}
                    onSelectParticipant={this.selectParticipant}
                    selectedParticipant={participants?.find(
                      p => p.uuid === selectedParticipantUuid
                    )}
                    participantPagination={{
                      onNextPage: () => onNextParticipantPage(participantEndCursor),
                      hasNextPage: participantListHasNextPage,
                    }}
                    coachPagination={{
                      onNextPage: () => onNextCoachPage(coachEndCursor),
                      hasNextPage: coachListHasNextPage,
                    }}
                    onCreateCoachingSession={this.handleCreateCoachingSession}
                    onNameQueryChanged={(newText: string) => {
                      this.nameQueryChanged(newText);
                    }}
                    nameQuery={this.state.nameSearchInputText}
                  />
                );
              }}
            </GetParticipantsContainer>
          );
        }}
      </GetCoachesContainer>
    );
  }
}
